import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import DefaultEditor from "react-simple-wysiwyg";
import { all_routes } from "../../Router/all_routes";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import { useSelector } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";

import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { isStoreChargingTax } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import {
  Select as ShadcnSelect,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"; // Renamed to avoid config with react-select
import { ReactSelect } from "@/components/ui/react-select";
import {
  Plus,
  Save,
  X,
  Wrench,
  User,
  Package,
  Settings,
  PlusCircle,
  Activity,
  Calendar,
  Tag,
  Clock,
  Edit,
  Info,
  ShoppingCart,
  Trash2,
  ChevronLeft,
  Loader2,
  Layers,
  ScanBarcode,
} from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import PDFModal from "./components/PDFModal";
import InventorySelectorModal from "../sales/components/pos/InventorySelectorModal";
import CustomerModal from "../../components/modals/CustomerModal";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const EditRepairJob = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // State Declarations
  const [repairJob, setRepairJob] = useState({
    customer_id: "",
    repair_category_ids: [],
    technician_id: "",
    store_id: "",
    item_description: "",
    serial_number: "",
    device_password: "",
    issue_description: "",
    diagnosis: "",
    estimated_cost: "",
    priority: "medium",
    estimated_completion_date: "",
    customer_notes: "",
    technician_notes: "",
    selectedParts: [],
    laborEntries: [],
    status: "",
  });

  const [specialPartsModalOpen, setSpecialPartsModalOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState("");

  const [brandSearchInput, setBrandSearchInput] = useState("");
  const [brandSearchTerm, setBrandSearchTerm] = useState("");
  const [modelSearchInput, setModelSearchInput] = useState("");
  const [modelSearchTerm, setModelSearchTerm] = useState("");
  const [supplierSearchInput, setSupplierSearchInput] = useState("");
  const [supplierSearchTerm, setSupplierSearchTerm] = useState("");

  const [brandLabels, setBrandLabels] = useState({});
  const [modelLabels, setModelLabels] = useState({});
  const [supplierLabels, setSupplierLabels] = useState({});

  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [technicianSearchInput, setTechnicianSearchInput] = useState("");
  const [technicianSearchTerm, setTechnicianSearchTerm] = useState("");

  const [customerLabels, setCustomerLabels] = useState({});
  const [technicianLabels, setTechnicianLabels] = useState({});

  const [specialPartData, setSpecialPartData] = useState({
    item_name: "",
    brand_id: "",
    model_id: "",
    required_quantity: 1,
    unit_cost: 0,
    retail_price: "",
    supplier_id: "",
    order_link: "",
    tracking_id: "",
    order_notes: "",
    order_date: "",
    is_tax_exclusive: false,
    has_purchase_order: false,
    purchase_order_id: "",
    status: "pending",
  });

  const [showInventorySelector, setShowInventorySelector] = useState(false);
  const [inventorySelectorTarget, setInventorySelectorTarget] = useState(null);
  const [inventorySelectorQuantity, setInventorySelectorQuantity] = useState(1);
  const [inventorySelectorItems, setInventorySelectorItems] = useState([]);

  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);

  const [newCategory, setNewCategory] = useState({
    name: "",
    description: "",
    is_active: true,
    sort_order: 0,
  });

  const [newBrand, setNewBrand] = useState({
    name: "",
    description: "",
    status: "active",
  });

  const [newModel, setNewModel] = useState({
    name: "",
    brand_id: "",
    description: "",
    status: "active",
  });

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [currentRepairData, setCurrentRepairData] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);


  const queryClient = useQueryClient();
  const storeData = useSelector((state) => state.current_store);

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (perm) => {
    return permissions && permissions.includes(perm);
  };

  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  // Queries
  const { data: jobData, isLoading: isJobLoading } = useQuery({
    queryKey: ["repair-job", id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.REPAIRS.REPAIR_DETAILS(id), {
        params: {
          with: [
            "parts.product",
            "laborEntries",
            "repairCategories",
            "customer",
            "technician",
          ],
        },
      });
      return res.data.data;
    },
    enabled: !!id,
  });

  const { data: customers = [], isFetching: isCustomersFetching } = useQuery({
    queryKey: ["customers", customerSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(`/customer`, {
        params: {
          q: customerSearchTerm || undefined,
          perPage: 15,
          per_page: 15,
        },
      });
      return res.data.data || [];
    },
    staleTime: 60 * 1000 * 10,
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["repair-categories"],
    queryFn: async () => {
      const res = await apiClient.get(`/category_repair`);
      return res.data.data || [];
    },
    staleTime: 60 * 1000 * 10,
  });

  const { data: technicians = [], isFetching: isTechniciansFetching } =
    useQuery({
      queryKey: ["technicians", technicianSearchTerm],
      queryFn: async () => {
        const res = await apiClient.get(`/employee`, {
          params: {
            search: technicianSearchTerm || undefined,
            perPage: 15,
            per_page: 15,
          },
        });
        return res.data.data || [];
      },
      staleTime: 60 * 1000 * 60,
    });

  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get(`/store`);
      return res.data.data || [];
    },
    staleTime: 60 * 1000 * 60,
  });

  const { data: products = [], isFetching: productSearchLoading } = useQuery({
    queryKey: ["all-pos-products", productSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
        params: {
          per_page: 20,
          search: productSearchTerm || undefined,
          with: [
            "brand",
            "model",
            "unit",
            "categories",
            "serial_numbers",
            "batches",
          ],
        },
      });

      const pageData = res.data.data || [];
      // Filter products that are intended for POS (saleable parts)
      return pageData.filter((p) => p.display_on_pos);
    },
    keepPreviousData: true,
  });

  const existingPartProductIds = useMemo(
    () =>
      (repairJob.selectedParts || []).map((p) => p.product_id).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [repairJob.selectedParts.length],
  );

  const { data: existingPartProducts = [] } = useQuery({
    queryKey: ["edit-repair-part-products", existingPartProductIds.join(",")],
    queryFn: async () => {
      const results = await Promise.all(
        existingPartProductIds.map((id) =>
          apiClient
            .get(API_ENDPOINTS.INVENTORY.POS_PRODUCT_DETAILS(id))
            .then((r) => r.data.data)
            .catch(() => null),
        ),
      );
      return results.filter(Boolean);
    },
    enabled: existingPartProductIds.length > 0,
    staleTime: Infinity,
  });

  useQuery({
    queryKey: ["customer-groups"],
    queryFn: async () => {
      const res = await apiClient.get(`/customer_group`);
      return res.data.data || [];
    },
    staleTime: 60 * 1000 * 60,
  });

  const { data: suppliers = [], isFetching: isSuppliersFetching } = useQuery({
    queryKey: ["suppliers", supplierSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(`/supplier`, {
        params: { search: supplierSearchTerm || undefined },
      });
      return res.data.data || [];
    },
    staleTime: 60 * 1000 * 60,
  });

  const { data: brands = [], isFetching: isBrandsFetching } = useQuery({
    queryKey: ["brands", brandSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(`/brand`, {
        params: { search: brandSearchTerm || undefined },
      });
      return res.data.data || [];
    },
    enabled: hasPermission("brand read"),
    staleTime: 60 * 1000 * 60,
  });

  const { data: models = [], isFetching: isModelsFetching } = useQuery({
    queryKey: ["models", specialPartData.brand_id, modelSearchTerm],
    queryFn: async () => {
      if (!specialPartData.brand_id) return [];
      const res = await apiClient.get(
        `/brand/${specialPartData.brand_id}/model`,
        {
          params: { search: modelSearchTerm || undefined },
        },
      );
      return res.data.data || res.data || [];
    },
    enabled: !!specialPartData.brand_id && hasPermission("brand model read"),
    staleTime: 60 * 1000 * 10,
  });

  const { data: purchaseOrders = [] } = useQuery({
    queryKey: ["purchase-orders"],
    queryFn: async () => {
      const res = await apiClient.get(`/purchase_order`);
      return res.data.data || [];
    },
    staleTime: 60 * 1000 * 5,
  });

  // Mutations

  const addCategoryMutation = useMutation({
    mutationFn: async (categoryData) => {
      const response = await apiClient.post(
        `/category_repair`,
        categoryData,
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["repair-categories"] });
      setRepairJob((prev) => ({
        ...prev,
        repair_category_ids: [...(prev.repair_category_ids || []), data.id],
      }));
      setCategoryModalOpen(false);
      setNewCategory({
        name: "",
        description: "",
        is_active: true,
        sort_order: 0,
      });
      toast.success("Repair category added successfully");
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to add repair category",
      );
    },
  });

  const addBrandMutation = useMutation({
    mutationFn: async (brandData) => {
      const res = await apiClient.post(`/brand`, brandData);
      return res.data.data;
    },
    onSuccess: (newBrandData) => {
      queryClient.invalidateQueries({ queryKey: ["brands"] });
      toast.success("Brand created successfully!");
      setBrandLabels((prev) => ({
        ...prev,
        [newBrandData.id.toString()]: newBrandData.name,
      }));
      setSpecialPartData((prev) => ({
        ...prev,
        brand_id: newBrandData.id.toString(),
      }));
      setNewBrand({ name: "", description: "", status: "active" });
      setBrandModalOpen(false);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to create brand");
    },
  });

  const addModelMutation = useMutation({
    mutationFn: async (modelData) => {
      const res = await apiClient.post(
        `/brand/${modelData.brand_id}/model`,
        modelData,
      );
      return res.data.data;
    },
    onSuccess: (newModelData) => {
      queryClient.invalidateQueries({ queryKey: ["models"] });
      toast.success("Model created successfully!");
      setModelLabels((prev) => ({
        ...prev,
        [newModelData.id.toString()]: newModelData.name,
      }));
      setSpecialPartData((prev) => ({
        ...prev,
        model_id: newModelData.id.toString(),
      }));
      setNewModel({
        name: "",
        brand_id: "",
        description: "",
        status: "active",
      });
      setModelModalOpen(false);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to create model");
    },
  });

  const saveRepairJobParts = async (repairJobId, selectedParts) => {
    try {
      const partsPromises = selectedParts.map((part) => {
        const partData = {
          product_id: part.product_id,
          inventory_batch_id: part.inventory_batch_id || null,
          quantity_used: parseFloat(part.quantity_used) || 1,
          unit_cost: parseFloat(part.unit_cost) || 0,
          notes: part.notes || null,
          serial_numbers:
            Array.isArray(part.serial_numbers) && part.serial_numbers.length > 0
              ? part.serial_numbers
              : null,
        };

        return apiClient.post(
          `/repair-jobs/${repairJobId}/parts`,
          partData,
        );
      });

      await Promise.all(partsPromises);
    } catch (error) {
      console.error("Error saving parts:", error);
      toast.warning("Warning", {
        description: "Repair job updated but some parts could not be synced",
      });
    }
  };

  const saveRepairJobLabor = async (repairJobId, laborEntries) => {
    try {
      const laborPromises = laborEntries.map((entry) => {
        const laborData = {
          technician_id: entry.technician_id,
          start_time: entry.start_time,
          end_time: entry.end_time,
          hourly_rate: entry.hourly_rate,
          description: entry.description,
        };

        return apiClient.post(
          `/repair-jobs/${repairJobId}/labor`,
          laborData,
        );
      });

      await Promise.all(laborPromises);
    } catch (error) {
      console.error("Error saving labor entries:", error);
    }
  };

  const updateRepairJobMutation = useMutation({
    mutationFn: async (repairJobData) => {
      const { selectedParts, laborEntries, ...mainData } = repairJobData;
      const response = await apiClient.put(
        API_ENDPOINTS.REPAIRS.REPAIR_DETAILS(id),
        mainData,
      );

      // Only post NEW parts/labor (not those loaded from existing job data)
      // Existing parts/labor are marked with _existing: true when loaded
      const newParts = (selectedParts || []).filter((p) => !p._existing);
      if (newParts.length > 0) {
        await saveRepairJobParts(id, newParts);
      }
      const newLabor = (laborEntries || []).filter((l) => !l._existing);
      if (newLabor.length > 0) {
        await saveRepairJobLabor(id, newLabor);
      }

      return response.data.data;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["repair-job", id] });
      queryClient.invalidateQueries({ queryKey: ["repair-jobs"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Something went wrong");
    },
  });

  const createSpecialPartMutation = useMutation({
    mutationFn: async (specialPartData) => {
      const response = await apiClient.post(
        API_ENDPOINTS.REPAIRS.REPAIR_JOB_SPECIAL_PARTS(
          specialPartData.repair_job_id,
        ),
        specialPartData,
      );
      return response.data;
    },
    onSuccess: () => {
      toast.success("Special part order created successfully!");
      setSpecialPartData({
        item_name: "",
        brand_id: "",
        model_id: "",
        required_quantity: 1,
        unit_cost: 0,
        retail_price: "",
        supplier_id: "",
        order_link: "",
        tracking_id: "",
        order_notes: "",
        order_date: "",
        is_tax_exclusive: false,
        has_purchase_order: false,
        purchase_order_id: "",
        status: "pending",
      });
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.message || "Failed to create special part order",
      );
    },
  });

  const isLoading = updateRepairJobMutation.isPending || isJobLoading;
  const modalLoading =
    addCategoryMutation.isPending ||
    createSpecialPartMutation.isPending ||
    addBrandMutation.isPending ||
    addModelMutation.isPending;

  // Effects
  useEffect(() => {
    const timer = setTimeout(() => {
      setBrandSearchTerm(brandSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [brandSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setModelSearchTerm(modelSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [modelSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSupplierSearchTerm(supplierSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [supplierSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCustomerSearchTerm(customerSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setTechnicianSearchTerm(technicianSearchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [technicianSearchInput]);

  useEffect(() => {
    if (customers.length > 0) {
      setCustomerLabels((prev) => {
        const next = { ...prev };
        customers.forEach((c) => {
          let displayName = c.full_name;
          if (c.customer_type !== "individual" && c.company) {
            displayName = c.company;
          }
          const labels = [displayName, c.phone].filter(Boolean);
          next[String(c.id)] = labels.join(" - ");
        });
        return next;
      });
    }
  }, [customers]);

  useEffect(() => {
    if (technicians.length > 0) {
      setTechnicianLabels((prev) => {
        const next = { ...prev };
        technicians.forEach((t) => {
          next[String(t.id)] = t.full_name || t.name;
        });
        return next;
      });
    }
  }, [technicians]);

  useEffect(() => {
    if (brands.length > 0) {
      setBrandLabels((prev) => {
        const next = { ...prev };
        brands.forEach((b) => {
          next[String(b.id)] = b.name;
        });
        return next;
      });
    }
  }, [brands]);

  useEffect(() => {
    if (models.length > 0) {
      setModelLabels((prev) => {
        const next = { ...prev };
        models.forEach((m) => {
          next[String(m.id)] = m.name;
        });
        return next;
      });
    }
  }, [models]);

  useEffect(() => {
    if (suppliers.length > 0) {
      setSupplierLabels((prev) => {
        const next = { ...prev };
        suppliers.forEach((s) => {
          next[String(s.id)] = s.name;
        });
        return next;
      });
    }
  }, [suppliers]);

  useEffect(() => {
    if (jobData) {
      if (jobData.customer) {
        let displayName = jobData.customer.full_name || jobData.customer.name;
        if (
          jobData.customer.customer_type !== "individual" &&
          jobData.customer.company
        ) {
          displayName = jobData.customer.company;
        }
        const labelText = [displayName, jobData.customer.phone]
          .filter(Boolean)
          .join(" - ");
        setCustomerLabels((prev) => ({
          ...prev,
          [String(jobData.customer.id)]: labelText,
        }));
      }
      if (jobData.technician) {
        setTechnicianLabels((prev) => ({
          ...prev,
          [String(jobData.technician.id)]:
            jobData.technician.full_name || jobData.technician.name,
        }));
      }
      setRepairJob({
        customer_id: jobData.customer_id || "",
        repair_category_ids:
          jobData.repair_categories?.map((c) => c.id) ||
          jobData.repair_category_ids ||
          [],
        technician_id: jobData.technician_id || "",
        store_id: jobData.store_id || "",
        item_description: jobData.item_description || "",
        serial_number: jobData.serial_number || "",
        device_password: jobData.device_password || "",
        issue_description: jobData.issue_description || "",
        diagnosis: jobData.diagnosis || "",
        estimated_cost: jobData.estimated_cost || "",
        priority:
          typeof jobData.priority === "object"
            ? jobData.priority.value
            : jobData.priority || "medium",
        estimated_completion_date:
          jobData.estimated_completion_date?.slice(0, 16) || "",
        customer_notes: jobData.customer_notes || "",
        technician_notes: jobData.technician_notes || "",
        selectedParts:
          jobData.parts?.map((p) => ({
            _existing: true, // Prevents re-posting on save - existing parts managed via dashboard
            product_id: p.product_id,
            product_name: p.product?.name || "",
            sku: p.product?.sku || "",
            tracking_type:
              p.product?.tracking_type ||
              (p.product?.is_serialized ? "serial" : "none"),
            inventory_batch_id: p.inventory_batch_id,
            quantity_used: p.quantity_used,
            unit_cost: p.unit_cost,
            notes: p.notes,
            serial_numbers: p.serial_numbers || [],
          })) || [],
        laborEntries:
          jobData.labor_entries?.map((l) => ({
            _existing: true, // Prevents re-posting on save - existing labor managed via dashboard
            technician_id: l.technician_id,
            start_time: l.start_time?.slice(0, 16),
            end_time: l.end_time?.slice(0, 16),
            hourly_rate: l.hourly_rate,
            description: l.description,
          })) || [],
        status:
          typeof jobData.status === "object"
            ? jobData.status.value
            : jobData.status || "",
      });
    }
  }, [jobData]);

  const handleOpenInventorySelector = (index, trackingType) => {
    const part = repairJob.selectedParts[index];
    setInventorySelectorItems(
      trackingType === "serial"
        ? part.available_serials || []
        : part.available_batches || [],
    );
    setInventorySelectorTarget({ partIndex: index, type: trackingType });
    setInventorySelectorQuantity(parseFloat(part.quantity_used) || 1);
    setShowInventorySelector(true);
  };

  const handleInventorySelect = (selection) => {
    const idx = inventorySelectorTarget?.partIndex;
    if (idx == null) return;
    const updatedParts = [...repairJob.selectedParts];
    if (inventorySelectorTarget.type === "standard") {
      updatedParts[idx].inventory_batch_id = selection;
      updatedParts[idx].inventory_batch_number =
        inventorySelectorItems.find((b) => b.id === selection)?.batch_number ||
        "";
    } else if (inventorySelectorTarget.type === "serial") {
      updatedParts[idx].serial_numbers = selection;
    }
    setRepairJob({ ...repairJob, selectedParts: updatedParts });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRepairJob({
      ...repairJob,
      [name]: value,
    });
  };

  const handleCategoryChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewCategory({
      ...newCategory,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleAddCategory = async () => {
    if (!newCategory.name) {
      toast.error("Category name is required");
      return;
    }
    addCategoryMutation.mutate(newCategory);
  };

  const handleAddBrand = async () => {
    if (!newBrand.name) {
      toast.error("Brand name is required");
      return;
    }
    addBrandMutation.mutate(newBrand);
  };

  const handleOpenModelModal = () => {
    if (!specialPartData.brand_id) {
      toast.error("Please select a brand first");
      return;
    }
    setNewModel((prev) => ({
      ...prev,
      brand_id: specialPartData.brand_id,
    }));
    setModelModalOpen(true);
  };

  const handleAddModel = async () => {
    if (!newModel.name) {
      toast.error("Model name is required");
      return;
    }
    addModelMutation.mutate(newModel);
  };

  const addLaborEntry = () => {
    const newEntry = {
      technician_id: "",
      start_time: "",
      end_time: "",
      hourly_rate: "",
      description: "",
    };
    setRepairJob({
      ...repairJob,
      laborEntries: [...repairJob.laborEntries, newEntry],
    });
  };

  const updateLaborEntry = (index, field, value) => {
    const updatedEntries = [...repairJob.laborEntries];
    updatedEntries[index][field] = value;

    // Auto-fill hourly rate when technician is selected
    if (field === "technician_id" && value) {
      const selectedTechnician = technicians.find((t) => t.id === value);
      if (selectedTechnician && selectedTechnician.hourly_rate) {
        updatedEntries[index].hourly_rate = selectedTechnician.hourly_rate;
      }
    }

    setRepairJob({
      ...repairJob,
      laborEntries: updatedEntries,
    });
  };

  const removeLaborEntry = (index) => {
    const updatedEntries = repairJob.laborEntries.filter((_, i) => i !== index);
    setRepairJob({
      ...repairJob,
      laborEntries: updatedEntries,
    });
  };

  const calculateLaborHours = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end - start;
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours > 0 ? diffHours : 0;
  };

  const validate = () => {
    if (!repairJob.customer_id) {
      toast.error("Customer is required");
      return false;
    }
    if (
      !repairJob.repair_category_ids ||
      repairJob.repair_category_ids.length === 0
    ) {
      toast.error("At least one repair category is required");
      return false;
    }
    if (!repairJob.store_id) {
      toast.error("Store is required");
      return false;
    }
    if (!repairJob.item_description) {
      toast.error("Item description is required");
      return false;
    }
    if (!repairJob.issue_description) {
      toast.error("Issue description is required");
      return false;
    }
    for (let i = 0; i < (repairJob.laborEntries || []).length; i++) {
      const entry = repairJob.laborEntries[i];
      if (entry.start_time && entry.end_time) {
        const start = new Date(entry.start_time);
        const end = new Date(entry.end_time);
        if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) {
          toast.error(
            `Labor entry #${i + 1}: End time must be after start time`,
          );
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    try {
      await updateRepairJobMutation.mutateAsync(repairJob);
      if (specialPartData.item_name) {
        await handleSubmitSpecialPart(repairJob.id || id);
      }
      toast.success("Repair job updated successfully");
      navigate(all_routes.repairDashboard);
    } catch {
      // Error is handled in mutation onError
    }
  };

  const handleGeneratePDFReport = async (id, type = "invoice") => {
    try {
      setPdfGenerating(true);
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }

      const endpoint =
        type === "job-note"
          ? "job-note"
          : type === "job-label"
            ? "job-label"
            : "invoice";

      const response = await apiClient.get(
        `/reports/pdf/repairs/${id}/${endpoint}`,
        { responseType: "blob" },
      );

      const blobUrl = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      setPdfBlobUrl(blobUrl);
    } catch (error) {
      console.error("PDF Report error:", error);
      toast.error("Error", {
        description:
          error.response?.data?.message || "Failed to generate PDF report",
      });
    } finally {
      setPdfGenerating(false);
    }
  };

  const printJobNote = async (id) => {
    try {
      setPdfGenerating(true);
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
      const response = await apiClient.get(
        `/reports/pdf/repairs/${id}/job-note`,
        { responseType: "blob" },
      );
      const blobUrl = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      setPdfBlobUrl(blobUrl);
    } catch (error) {
      console.error("Error printing job note:", error);
      toast.error("Print Error", {
        description: "Failed to print job note. Please try again.",
      });
    } finally {
      setPdfGenerating(false);
    }
  };

  const printJobLabel = async (id) => {
    try {
      setPdfGenerating(true);
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }
      const response = await apiClient.get(
        `/reports/pdf/repairs/${id}/job-label`,
        { responseType: "blob" },
      );
      const blobUrl = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" }),
      );
      setPdfBlobUrl(blobUrl);
    } catch (error) {
      console.error("Error printing job label:", error);
      toast.error("Print Error", {
        description: "Failed to print job label. Please try again.",
      });
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleClosePDFModal = () => {
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
    setPdfGenerating(false);
    setPdfModalOpen(false);
    setCurrentRepairData(null);
    navigate(all_routes.repairDashboard);
  };

  const handleSpecialPartChange = (e) => {
    const { name, value } = e.target;

    if (name === "brand_id") {
      setSpecialPartData((prev) => ({
        ...prev,
        [name]: value,
        model_id: "",
      }));
    } else {
      setSpecialPartData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSpecialPartBooleanChange = (e) => {
    const { name, checked } = e.target;

    if (name === "has_purchase_order" && !checked) {
      setSpecialPartData((prev) => ({
        ...prev,
        [name]: checked,
        purchase_order_id: "",
      }));
    } else {
      setSpecialPartData((prev) => ({
        ...prev,
        [name]: checked,
      }));
    }
  };

  const getFilteredModels = () => {
    return models;
  };

  const handleOpenSpecialPartsModal = () => {
    setSpecialPartData({
      item_name: "",
      brand_id: "",
      model_id: "",
      required_quantity: 1,
      unit_cost: 0,
      retail_price: "",
      supplier_id: "",
      order_link: "",
      tracking_id: "",
      order_notes: "",
      order_date: "",
      is_tax_exclusive: false,
      has_purchase_order: false,
      purchase_order_id: "",
      status: "pending",
    });
    setSpecialPartsModalOpen(true);
  };

  const handleCloseSpecialPartsModal = () => {
    setSpecialPartsModalOpen(false);
  };

  const handleSubmitSpecialPart = async (repairJobId) => {
    if (
      !specialPartData.item_name ||
      !specialPartData.brand_id ||
      !specialPartData.model_id ||
      !specialPartData.supplier_id
    ) {
      toast.error("Validation Error", {
        description: "Please fill in all required fields in special part order",
      });
      return;
    }

    await createSpecialPartMutation.mutateAsync({
      ...specialPartData,
      repair_job_id: repairJobId,
      brand_id: parseInt(specialPartData.brand_id),
      model_id: parseInt(specialPartData.model_id),
      required_quantity: parseInt(specialPartData.required_quantity),
      unit_cost: parseFloat(specialPartData.unit_cost),
      retail_price: specialPartData.retail_price
        ? parseFloat(specialPartData.retail_price)
        : null,
    });
  };

  const customerOptions = customers.map((customer) => {
    let displayName = customer.full_name;

    if (customer.customer_type !== "individual" && customer.company) {
      displayName = customer.company;
    }

    const labels = [displayName, customer.phone].filter(Boolean);

    return {
      value: customer.id,
      label: labels.join(" - "),
    };
  });

  const hierarchicalCategories = React.useMemo(() => {
    if (!categories || categories.length === 0) return [];

    const categoryMap = new Map();
    categories.forEach((cat) => {
      categoryMap.set(cat.id, { ...cat, children: [] });
    });

    const rootCategories = [];
    categories.forEach((cat) => {
      const category = categoryMap.get(cat.id);
      if (cat.parent_id && categoryMap.has(cat.parent_id)) {
        categoryMap.get(cat.parent_id).children.push(category);
      } else {
        rootCategories.push(category);
      }
    });

    const flatten = (items, depth = 0) => {
      const result = [];
      items.forEach((item) => {
        result.push({ ...item, depth });
        if (item.children.length > 0) {
          result.push(...flatten(item.children, depth + 1));
        }
      });
      return result;
    };

    return flatten(rootCategories);
  }, [categories]);

  const toggleCategory = (categoryId) => {
    setRepairJob((prev) => {
      const currentIds = [...(prev.repair_category_ids || [])];
      const index = currentIds.indexOf(categoryId);
      if (index === -1) {
        currentIds.push(categoryId);
      } else {
        currentIds.splice(index, 1);
      }
      return { ...prev, repair_category_ids: currentIds };
    });
  };

  const technicianOptions = technicians.map((tech) => ({
    value: tech.id,
    label: tech.full_name,
  }));

  const storeOptions = stores.map((store) => ({
    value: store.id,
    label: store.name,
  }));

  const { partsTotal, partsTaxTotal } = repairJob.selectedParts.reduce(
    (acc, part) => {
      const product = products.find((p) => p.id === part.product_id);
      const basePrice =
        parseFloat(part.quantity_used || 0) * parseFloat(part.unit_cost || 0);

      if (!isStoreChargingTax(storeData) || !product || !product.is_taxable) {
        return {
          partsTotal: acc.partsTotal + basePrice,
          partsTaxTotal: acc.partsTaxTotal,
        };
      }

      const taxRate = parseFloat(storeData.tax_percentage) || 0;
      const taxMethod = product.tax_method || "exclusive";

      if (taxMethod === "inclusive") {
        const taxAmount = basePrice - basePrice / (1 + taxRate / 100);
        return {
          partsTotal: acc.partsTotal + basePrice,
          partsTaxTotal: acc.partsTaxTotal + taxAmount,
        };
      } else {
        const taxAmount = (basePrice * taxRate) / 100;
        return {
          partsTotal: acc.partsTotal + basePrice + taxAmount,
          partsTaxTotal: acc.partsTaxTotal + taxAmount,
        };
      }
    },
    { partsTotal: 0, partsTaxTotal: 0 },
  );

  const laborTotal = repairJob.laborEntries.reduce((total, entry) => {
    const hours = calculateLaborHours(entry.start_time, entry.end_time);
    return total + hours * parseFloat(entry.hourly_rate || 0);
  }, 0);

  const grandTotal = partsTotal + laborTotal;

  if (isJobLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content p-6">
        <PageHeader>
          <PageHeaderHeading
            title={`Edit Repair Job: ${jobData?.repair_number || ""}`}
            subtitle="Update repair job information, parts and labor"
          />
          <PageHeaderActions>
            <Button
              variant="outline"
              onClick={() => navigate(all_routes.repairDashboard)}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? (
                <Spinner className="mr-2 h-4 w-4" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {isLoading ? "Processing..." : "Save Changes"}
            </Button>
          </PageHeaderActions>
        </PageHeader>

        <form onSubmit={(e) => e.preventDefault()}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Basic Information */}
              <Card>
                <CardHeader className="flex flex-row items-center space-x-2 space-y-0">
                  <User className="h-5 w-5 text-primary" />
                  <CardTitle>Customer Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label className="fw-medium">
                        Customer <span className="text-destructive">*</span>
                      </Label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <ReactSelect
                            className="basic-single"
                            classNamePrefix="select"
                            onInputChange={(value, { action }) => {
                              if (action === "input-change") {
                                setCustomerSearchInput(value);
                              }
                            }}
                            isLoading={isCustomersFetching}
                            filterOption={() => true}
                            options={customerOptions}
                            placeholder="Select Customer"
                            value={
                              repairJob.customer_id
                                ? {
                                  value: repairJob.customer_id,
                                  label:
                                    customerLabels[
                                    String(repairJob.customer_id)
                                    ] ||
                                    customerOptions.find(
                                      (opt) =>
                                        opt.value === repairJob.customer_id,
                                    )?.label ||
                                    "Select Customer",
                                }
                                : null
                            }
                            onChange={(option) =>
                              setRepairJob({
                                ...repairJob,
                                customer_id: option ? option.value : "",
                              })
                            }
                            isClearable
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                          />
                        </div>
                        {hasPermission("customer create") && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => setCustomerModalOpen(true)}
                          >
                            <Plus size={18} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Device Information */}
              <Card>
                <CardHeader className="flex flex-row items-center space-x-2 space-y-0">
                  <Package className="h-5 w-5 text-primary" />
                  <CardTitle>Device Information</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2 space-y-2">
                      <Label className="fw-medium">
                        Item Description{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        type="text"
                        name="item_description"
                        value={repairJob.item_description}
                        onChange={handleChange}
                        placeholder="iPhone 14 Pro Max, MacBook Air M2, etc."
                        maxLength={255}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="fw-medium">Serial Number</Label>
                      <Input
                        type="text"
                        name="serial_number"
                        value={repairJob.serial_number}
                        onChange={handleChange}
                        placeholder="Device Serial Number"
                      />
                    </div>
                    {hasPermission("repair job view_device_password") && (
                      <div className="space-y-2">
                        <Label className="fw-medium">
                          Access Code / Password
                        </Label>
                        <Input
                          type="text"
                          name="device_password"
                          value={repairJob.device_password}
                          onChange={handleChange}
                          placeholder="PIN or Password"
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Issue & Diagnosis */}
              <Card>
                <CardHeader className="flex flex-row items-center space-x-2 space-y-0">
                  <Settings className="h-5 w-5 text-primary" />
                  <CardTitle>Issue & Diagnosis</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="fw-medium mb-3">
                        Reported Issue{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <div className="border rounded-md overflow-hidden">
                        <DefaultEditor
                          value={repairJob.issue_description}
                          onChange={(e) =>
                            setRepairJob({
                              ...repairJob,
                              issue_description: e.target.value,
                            })
                          }
                          containerProps={{
                            style: { resize: "vertical", height: "200px" },
                          }}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="fw-medium mb-3">
                        Internal Diagnosis
                      </Label>
                      <div className="border rounded-md overflow-hidden">
                        <DefaultEditor
                          value={repairJob.diagnosis}
                          onChange={(e) =>
                            setRepairJob({
                              ...repairJob,
                              diagnosis: e.target.value,
                            })
                          }
                          containerProps={{
                            style: { resize: "vertical", height: "200px" },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Parts Selection */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <div className="flex items-center space-x-2">
                    <Package className="h-5 w-5 text-primary" />
                    <CardTitle>Parts & Components</CardTitle>
                  </div>
                  {hasPermission("special part create") && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleOpenSpecialPartsModal}
                    >
                      <PlusCircle size={14} className="mr-2 text-primary" />
                      Special Part
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {hasPermission("repair job part add") && (
                    <div className="mb-4 space-y-2">
                      <Label className="fw-medium">Inventory Search</Label>
                      <ReactSelect
                        placeholder="Search and add parts..."
                        onInputChange={(val) => setProductSearchTerm(val)}
                        options={products.map((p) => ({
                          value: p.id,
                          label: `${p.name}${p.brand ? ` (${p.brand.name})` : ""}${p.model ? ` - ${p.model.name}` : ""}${p.sku ? ` [${p.sku}]` : ""} - ${currencySymbol}${parseFloat(p.sell_price || 0).toFixed(2)}`,
                          product: p,
                        }))}
                        isLoading={productSearchLoading}
                        onChange={(option) => {
                          if (!option) return;
                          const product = option.product;
                          const alreadyExists = repairJob.selectedParts.find(
                            (p) => p.product_id === product.id,
                          );

                          if (alreadyExists) {
                            toast.error("This part is already added");
                            return;
                          }

                          // Filter out serial numbers already used in selectedParts
                          let availableSerials = product.serial_numbers || [];
                          if (
                            product.tracking_type === "serial" ||
                            product.is_serialized
                          ) {
                            const usedSerials = repairJob.selectedParts.reduce(
                              (acc, part) => {
                                if (
                                  part.product_id === product.id &&
                                  part.serial_numbers
                                ) {
                                  acc.push(
                                    ...part.serial_numbers.map((sn) =>
                                      typeof sn === "string"
                                        ? sn
                                        : sn.serial_number,
                                    ),
                                  );
                                }
                                return acc;
                              },
                              [],
                            );
                            availableSerials = availableSerials.filter(
                              (sn) =>
                                !usedSerials.includes(
                                  typeof sn === "string"
                                    ? sn
                                    : sn.serial_number,
                                ),
                            );
                          }

                          const newPart = {
                            product_id: product.id,
                            product_name: product.name || "",
                            sku: product.sku || "",
                            quantity_used: 1,
                            unit_cost: parseFloat(product.sell_price || 0),
                            inventory_batch_id: null,
                            serial_numbers: [],
                            tracking_type:
                              product.tracking_type ||
                              (product.is_serialized ? "serial" : "none"),
                            available_serials: availableSerials,
                            available_batches: product.batches || [],
                            notes: "",
                          };

                          setRepairJob({
                            ...repairJob,
                            selectedParts: [
                              ...repairJob.selectedParts,
                              newPart,
                            ],
                          });
                        }}
                        isClearable
                        value={null}
                      />
                    </div>
                  )}

                  {repairJob.selectedParts.length > 0 && (
                    <div className="rounded-md border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Part Name / Info</TableHead>
                            <TableHead className="w-[100px]">Qty</TableHead>
                            <TableHead className="w-[130px]">
                              Unit Price
                            </TableHead>
                            <TableHead className="w-[140px]">
                              Inventory
                            </TableHead>
                            <TableHead className="w-[120px] text-right">
                              Subtotal
                            </TableHead>
                            <TableHead className="w-[50px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {repairJob.selectedParts.map((part, index) => {
                            const product =
                              products.find((p) => p.id === part.product_id) ||
                              existingPartProducts.find(
                                (p) => p.id === part.product_id,
                              );
                            const subtotal =
                              parseFloat(part.quantity_used || 0) *
                              parseFloat(part.unit_cost || 0);

                            const trackingType =
                              part.tracking_type ||
                              product?.tracking_type ||
                              "none";

                            return (
                              <React.Fragment key={index}>
                                <TableRow className="border-b-0">
                                  <TableCell>
                                    <div className="font-medium">
                                      {part.product_name ||
                                        product?.name ||
                                        "Unknown Product"}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                      SKU: {part.sku || product?.sku || "N/A"}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <Input
                                      type="number"
                                      className="h-8 shadow-none"
                                      value={part.quantity_used}
                                      onChange={(e) => {
                                        const updatedParts = [
                                          ...repairJob.selectedParts,
                                        ];
                                        updatedParts[index].quantity_used =
                                          e.target.value;
                                        setRepairJob({
                                          ...repairJob,
                                          selectedParts: updatedParts,
                                        });
                                      }}
                                      min="0.01"
                                      step="0.01"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <div className="relative">
                                      <span className="absolute left-2 top-1.5 text-xs text-muted-foreground">
                                        {currencySymbol}
                                      </span>
                                      <Input
                                        type="number"
                                        className="h-8 pl-6 shadow-none"
                                        value={part.unit_cost}
                                        onChange={(e) => {
                                          const updatedParts = [
                                            ...repairJob.selectedParts,
                                          ];
                                          updatedParts[index].unit_cost =
                                            e.target.value;
                                          setRepairJob({
                                            ...repairJob,
                                            selectedParts: updatedParts,
                                          });
                                        }}
                                        min="0"
                                        step="0.01"
                                      />
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {trackingType === "none" ? (
                                      <Badge variant="outline">
                                        No Tracking
                                      </Badge>
                                    ) : (
                                      <div className="space-y-1">
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          className={`h-8 w-full text-xs ${(trackingType === "standard" &&
                                            !part.inventory_batch_id) ||
                                            (trackingType === "serial" &&
                                              (!part.serial_numbers ||
                                                part.serial_numbers.length <
                                                parseFloat(
                                                  part.quantity_used,
                                                )))
                                            ? "border-destructive text-destructive"
                                            : "border-emerald-500 text-emerald-600"
                                            }`}
                                          onClick={() =>
                                            handleOpenInventorySelector(
                                              index,
                                              trackingType,
                                            )
                                          }
                                        >
                                          {trackingType === "standard" ? (
                                            <>
                                              <Layers className="mr-1 h-3 w-3" />
                                              {part.inventory_batch_id
                                                ? part.inventory_batch_number ||
                                                "Selected"
                                                : "Select Batch"}
                                            </>
                                          ) : (
                                            <>
                                              <ScanBarcode className="mr-1 h-3 w-3" />
                                              {part.serial_numbers?.length || 0}
                                              /{part.quantity_used}
                                            </>
                                          )}
                                        </Button>
                                      </div>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-right font-medium">
                                    {currencySymbol}
                                    {subtotal.toFixed(2)}
                                  </TableCell>
                                  <TableCell>
                                    {hasPermission(
                                      "repair job part remove",
                                    ) && (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 text-destructive"
                                          onClick={() => {
                                            const updatedParts =
                                              repairJob.selectedParts.filter(
                                                (_, i) => i !== index,
                                              );
                                            setRepairJob({
                                              ...repairJob,
                                              selectedParts: updatedParts,
                                            });
                                          }}
                                        >
                                          <Trash2 size={14} />
                                        </Button>
                                      )}
                                  </TableCell>
                                </TableRow>
                                <TableRow className="border-t-0 bg-muted/20">
                                  <TableCell
                                    colSpan={6}
                                    className="pt-0 pb-2 px-4"
                                  >
                                    <Input
                                      placeholder="Notes for this part..."
                                      className="h-7 text-xs bg-transparent border-dashed shadow-none"
                                      value={part.notes || ""}
                                      onChange={(e) => {
                                        const updatedParts = [
                                          ...repairJob.selectedParts,
                                        ];
                                        updatedParts[index].notes =
                                          e.target.value;
                                        setRepairJob({
                                          ...repairJob,
                                          selectedParts: updatedParts,
                                        });
                                      }}
                                      maxLength={1000}
                                    />
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                      <div className="bg-muted/50 p-3 flex justify-between items-center border-t">
                        <span className="text-sm font-medium">
                          Parts Total:
                        </span>
                        <span className="font-bold">
                          {currencySymbol}
                          {partsTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Labor Entries */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <div className="flex items-center space-x-2">
                    <Clock className="h-5 w-5 text-primary" />
                    <CardTitle>Labor & Effort</CardTitle>
                  </div>
                  {hasPermission("repair job add_labor") && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addLaborEntry}
                    >
                      <Plus size={14} className="mr-2 text-primary" />
                      Add Labor
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  {repairJob.laborEntries.length === 0 ? (
                    <div className="text-center py-10 border-2 border-dashed rounded-lg bg-muted/30">
                      <Clock
                        size={40}
                        className="text-muted-foreground opacity-20 mx-auto mb-2"
                      />
                      <p className="text-muted-foreground text-sm">
                        No labor entries recorded yet
                      </p>
                      {hasPermission("repair job add_labor") && (
                        <Button
                          type="button"
                          variant="link"
                          className="text-primary mt-1"
                          onClick={addLaborEntry}
                        >
                          Add your first labor entry
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {repairJob.laborEntries.map((entry, index) => {
                        const laborHours = calculateLaborHours(
                          entry.start_time,
                          entry.end_time,
                        );
                        const laborCost =
                          laborHours * parseFloat(entry.hourly_rate || 0);

                        return (
                          <div
                            key={index}
                            className="border rounded-lg overflow-hidden border-border bg-card"
                          >
                            <div className="bg-muted px-3 py-2 flex justify-between items-center">
                              <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                                Entry #{index + 1}
                              </span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-destructive hover:bg-destructive/10"
                                onClick={() => removeLaborEntry(index)}
                              >
                                <X size={14} />
                              </Button>
                            </div>
                            <div className="p-4 grid grid-cols-1 md:grid-cols-12 gap-4">
                              <div className="md:col-span-12 lg:col-span-4 space-y-2">
                                <Label className="text-xs fw-medium">
                                  Technician
                                </Label>
                                <ReactSelect
                                  className="basic-single"
                                  classNamePrefix="select"
                                  onInputChange={(value) =>
                                    setTechnicianSearchInput(value)
                                  }
                                  isLoading={isTechniciansFetching}
                                  filterOption={() => true}
                                  options={technicianOptions}
                                  placeholder="Assign..."
                                  value={
                                    entry.technician_id
                                      ? {
                                        value: entry.technician_id,
                                        label:
                                          technicianLabels[
                                          String(entry.technician_id)
                                          ] ||
                                          technicianOptions.find(
                                            (opt) =>
                                              opt.value ===
                                              entry.technician_id,
                                          )?.label ||
                                          "Assign...",
                                      }
                                      : null
                                  }
                                  onChange={(opt) =>
                                    updateLaborEntry(
                                      index,
                                      "technician_id",
                                      opt?.value || "",
                                    )
                                  }
                                />
                              </div>
                              <div className="md:col-span-6 lg:col-span-3 space-y-2">
                                <Label className="text-xs fw-medium">
                                  Rate / Hour
                                </Label>
                                <div className="relative">
                                  <span className="absolute left-2 top-2 text-xs text-muted-foreground">
                                    {currencySymbol}
                                  </span>
                                  <Input
                                    type="number"
                                    className="h-9 pl-6"
                                    value={entry.hourly_rate}
                                    onChange={(e) =>
                                      updateLaborEntry(
                                        index,
                                        "hourly_rate",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </div>
                              </div>
                              <div className="md:col-span-6 lg:col-span-5 space-y-2">
                                <Label className="text-xs fw-medium">
                                  Subtotal
                                </Label>
                                <div className="h-9 flex items-center px-3 bg-primary/5 text-primary border border-primary/10 rounded-md font-bold text-sm">
                                  {currencySymbol} {laborCost.toFixed(2)}
                                </div>
                              </div>
                              <div className="md:col-span-6 space-y-2">
                                <Label className="text-xs fw-medium">
                                  Start Time
                                </Label>
                                <Input
                                  type="datetime-local"
                                  className="h-9"
                                  value={entry.start_time}
                                  onChange={(e) =>
                                    updateLaborEntry(
                                      index,
                                      "start_time",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                              <div className="md:col-span-6 space-y-2">
                                <Label className="text-xs fw-medium">
                                  End Time
                                </Label>
                                <Input
                                  type="datetime-local"
                                  className={`h-9 ${entry.start_time &&
                                    entry.end_time &&
                                    new Date(entry.end_time) <=
                                    new Date(entry.start_time)
                                    ? "border-destructive text-destructive focus-visible:ring-destructive"
                                    : ""
                                    }`}
                                  min={entry.start_time || undefined}
                                  value={entry.end_time}
                                  onChange={(e) =>
                                    updateLaborEntry(
                                      index,
                                      "end_time",
                                      e.target.value,
                                    )
                                  }
                                />
                                {entry.start_time &&
                                  entry.end_time &&
                                  new Date(entry.end_time) <=
                                  new Date(entry.start_time) && (
                                    <p className="text-xs text-destructive font-medium">
                                      End time must be after start time
                                    </p>
                                  )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="bg-primary text-primary-foreground p-3 rounded-md flex justify-between items-center">
                        <span className="text-sm font-medium">
                          Total Labor Contribution
                        </span>
                        <span className="text-xl font-bold">
                          {currencySymbol}
                          {laborTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Notes */}
              <Card>
                <CardHeader className="flex flex-row items-center space-x-2 space-y-0">
                  <Edit className="h-5 w-5 text-primary" />
                  <CardTitle>Internal & External Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="fw-medium mb-2 block">
                        Customer Facing Notes
                      </Label>
                      <div className="border rounded-md overflow-hidden">
                        <DefaultEditor
                          value={repairJob.customer_notes}
                          onChange={(e) =>
                            setRepairJob({
                              ...repairJob,
                              customer_notes: e.target.value,
                            })
                          }
                          containerProps={{ style: { height: "150px" } }}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="fw-medium mb-2 block">
                        Internal Technician Notes
                      </Label>
                      <div className="border rounded-md overflow-hidden">
                        <DefaultEditor
                          value={repairJob.technician_notes}
                          onChange={(e) =>
                            setRepairJob({
                              ...repairJob,
                              technician_notes: e.target.value,
                            })
                          }
                          containerProps={{ style: { height: "150px" } }}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar Column */}
            <div className="space-y-6">
              <div className="sticky top-6">
                {/* Actions Card */}
                <Card className="mb-6">
                  <CardHeader className="flex flex-row items-center space-x-2 space-y-0">
                    <Activity className="h-5 w-5 text-primary" />
                    <CardTitle>Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between text-sm py-2 border-b">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Activity size={14} />
                        Status:
                      </div>
                      <span className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium uppercase">
                        {repairJob.status || "Unknown"}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      <Button
                        type="button"
                        className="w-full justify-center gap-2"
                        onClick={handleSubmit}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Spinner className="h-4 w-4" />
                        ) : (
                          <Save size={18} />
                        )}
                        {isLoading ? "Processing..." : "Save Changes"}
                      </Button>

                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-center gap-2"
                        onClick={() => navigate(all_routes.repairDashboard)}
                      >
                        <X size={18} />
                        Discard changes
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Categories Card */}
                <Card className="mb-6">
                  <CardHeader className="flex flex-row items-center space-x-2 space-y-0">
                    <Tag className="h-5 w-5 text-primary" />
                    <CardTitle>Repair Categories</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2">
                      {hierarchicalCategories.length > 0 ? (
                        hierarchicalCategories.map((cat) => (
                          <div
                            key={cat.id}
                            className="flex items-center space-x-2"
                            style={{ marginLeft: `${cat.depth * 16}px` }}
                          >
                            <Checkbox
                              id={`cat-${cat.id}`}
                              checked={(
                                repairJob.repair_category_ids || []
                              ).includes(cat.id)}
                              onCheckedChange={() => toggleCategory(cat.id)}
                            />
                            <Label
                              htmlFor={`cat-${cat.id}`}
                              className="text-sm font-medium leading-none cursor-pointer"
                            >
                              {cat.name}
                            </Label>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted text-xs italic">
                          No categories found.
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="link"
                      className="p-0 h-auto text-xs mt-4"
                      onClick={() => setCategoryModalOpen(true)}
                    >
                      <Plus size={14} className="mr-1" />
                      Add New Category
                    </Button>
                  </CardContent>
                </Card>

                {/* Job Metadata Card */}
                <Card className="mb-6">
                  <CardHeader className="flex flex-row items-center space-x-2 space-y-0">
                    <Calendar className="h-5 w-5 text-primary" />
                    <CardTitle>Job Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-muted-foreground uppercase">
                        Store
                      </Label>
                      <ReactSelect
                        className="basic-single"
                        classNamePrefix="select"
                        options={storeOptions}
                        placeholder="Select Store..."
                        value={storeOptions.find(
                          (opt) => opt.value === repairJob.store_id,
                        )}
                        onChange={(opt) =>
                          setRepairJob({
                            ...repairJob,
                            store_id: opt?.value || "",
                          })
                        }
                        isClearable
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-muted-foreground uppercase">
                        Lead Technician
                      </Label>
                      <ReactSelect
                        className="basic-single"
                        classNamePrefix="select"
                        onInputChange={(value) =>
                          setTechnicianSearchInput(value)
                        }
                        isLoading={isTechniciansFetching}
                        filterOption={() => true}
                        options={technicianOptions}
                        value={
                          repairJob.technician_id
                            ? {
                              value: repairJob.technician_id,
                              label:
                                technicianLabels[
                                String(repairJob.technician_id)
                                ] ||
                                technicianOptions.find(
                                  (opt) =>
                                    opt.value === repairJob.technician_id,
                                )?.label ||
                                "Assign...",
                            }
                            : null
                        }
                        onChange={(opt) =>
                          setRepairJob({
                            ...repairJob,
                            technician_id: opt?.value || "",
                          })
                        }
                        isClearable
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-muted-foreground uppercase">
                        Service Priority
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        {["low", "medium", "high", "urgent"].map((p) => (
                          <Button
                            key={p}
                            type="button"
                            size="sm"
                            variant={
                              repairJob.priority === p ? "default" : "secondary"
                            }
                            className="capitalize text-xs h-8"
                            onClick={() =>
                              setRepairJob({ ...repairJob, priority: p })
                            }
                          >
                            {p}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-muted-foreground uppercase">
                        Est. Completion
                      </Label>
                      <Input
                        type="datetime-local"
                        name="estimated_completion_date"
                        value={repairJob.estimated_completion_date}
                        onChange={handleChange}
                        className="h-9"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-bold text-muted-foreground uppercase">
                        Estimated Cost ({currencySymbol})
                      </Label>
                      <Input
                        type="number"
                        name="estimated_cost"
                        value={repairJob.estimated_cost}
                        onChange={handleChange}
                        placeholder="0.00"
                        className="h-9"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Grand Total Card */}
                <Card className="bg-primary text-primary-foreground border-none">
                  <CardHeader className="pb-2">
                    <CardDescription className="text-primary-foreground/70 uppercase text-xs font-bold tracking-wider">
                      Estimated Repair Value
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold">
                      {currencySymbol}
                      {grandTotal.toFixed(2)}
                    </div>
                  </CardContent>
                  <CardHeader className="pt-0 border-t border-primary-foreground/10 mt-2">
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="opacity-70">Parts Total:</span>
                        <span>
                          {currencySymbol}
                          {partsTotal.toFixed(2)}
                        </span>
                      </div>
                      {partsTaxTotal > 0 && (
                        <div className="flex justify-between text-xs">
                          <span className="opacity-70">Tax Amount:</span>
                          <span>
                            {currencySymbol}
                            {partsTaxTotal.toFixed(2)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between text-xs">
                        <span className="opacity-70">Labor Total:</span>
                        <span>
                          {currencySymbol}
                          {laborTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              </div>
            </div>
          </div>
        </form>
      </div>

      <CustomerModal
        open={customerModalOpen}
        onOpenChange={setCustomerModalOpen}
        onSuccess={(createdCustomer) => {
          setRepairJob((prev) => ({
            ...prev,
            customer_id: createdCustomer.id,
          }));
          const displayName =
            createdCustomer.customer_type !== "individual" &&
              createdCustomer.company
              ? createdCustomer.company
              : createdCustomer.full_name;
          const labels = [displayName, createdCustomer.phone].filter(Boolean);
          setCustomerLabels((prev) => ({
            ...prev,
            [String(createdCustomer.id)]: labels.join(" - "),
          }));
          queryClient.invalidateQueries({ queryKey: ["customers"] });
        }}
      />

      {/* Add Category Modal */}
      <Dialog open={categoryModalOpen} onOpenChange={setCategoryModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Repair Category</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>
                Category Name <span className="text-destructive">*</span>
              </Label>
              <Input
                type="text"
                name="name"
                value={newCategory.name}
                onChange={handleCategoryChange}
                placeholder="Enter category name"
                maxLength={255}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <div className="border rounded-md overflow-hidden bg-background">
                <DefaultEditor
                  value={newCategory.description}
                  onChange={(e) =>
                    setNewCategory({
                      ...newCategory,
                      description: e.target.value,
                    })
                  }
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCategoryModalOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleAddCategory} disabled={modalLoading}>
              {modalLoading ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" /> Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PDF Modal */}
      <PDFModal
        show={pdfModalOpen}
        onHide={handleClosePDFModal}
        currentRepairData={currentRepairData}
        isGeneratingPDF={pdfGenerating}
        handleGeneratePDFReport={handleGeneratePDFReport}
        onPrintJobNote={printJobNote}
        onPrintJobLabel={printJobLabel}
        pdfBlobUrl={pdfBlobUrl}
        currencySymbol={currencySymbol}
        onClearPreview={() => {
          if (pdfBlobUrl) {
            URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl(null);
          }
        }}
      />

      {specialPartsModalOpen && (
        <Dialog
          open={specialPartsModalOpen}
          onOpenChange={handleCloseSpecialPartsModal}
        >
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto px-1">
            <DialogHeader className="px-5 pt-5">
              <DialogTitle>Order Special Part</DialogTitle>
            </DialogHeader>
            <div className="px-5">
              <Alert
                variant="info"
                className="bg-blue-50 text-blue-800 border-blue-200"
              >
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>Note:</strong> This will create a special part order
                  that will be linked to this repair job once created.
                </AlertDescription>
              </Alert>

              <Tabs defaultValue="general" className="w-full">
                <TabsList className="w-full justify-start">
                  <TabsTrigger value="general">General</TabsTrigger>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  <TabsTrigger value="settings">Settings</TabsTrigger>
                </TabsList>

                <TabsContent value="general" className="space-y-4 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>
                        Item Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        name="item_name"
                        value={specialPartData.item_name}
                        onChange={handleSpecialPartChange}
                        maxLength={255}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>
                        Brand <span className="text-destructive">*</span>
                      </Label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <ReactSelect
                            className="basic-single"
                            classNamePrefix="select"
                            onInputChange={(value) =>
                              setBrandSearchInput(value)
                            }
                            options={brands.map((b) => ({
                              value: String(b.id),
                              label: b.name,
                            }))}
                            value={
                              specialPartData.brand_id
                                ? {
                                  value: String(specialPartData.brand_id),
                                  label:
                                    brandLabels[
                                    String(specialPartData.brand_id)
                                    ] ||
                                    brands.find(
                                      (b) =>
                                        String(b.id) ===
                                        String(specialPartData.brand_id),
                                    )?.name ||
                                    "Select Brand",
                                }
                                : null
                            }
                            onChange={(opt) =>
                              handleSpecialPartChange({
                                target: {
                                  name: "brand_id",
                                  value: opt ? opt.value : "",
                                },
                              })
                            }
                            placeholder="Search Brand..."
                            isLoading={isBrandsFetching}
                            isClearable
                          />
                        </div>
                        {hasPermission("brand create") && (
                          <Button
                            size="icon"
                            onClick={() => setBrandModalOpen(true)}
                            type="button"
                          >
                            <Plus size={18} />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>
                        Model <span className="text-destructive">*</span>
                        <span
                          className="ml-1 text-muted-foreground"
                          title="First select a brand and then select a model"
                        >
                          <Info size={14} className="inline" />
                        </span>
                      </Label>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <ReactSelect
                            className="basic-single"
                            classNamePrefix="select"
                            onInputChange={(value) =>
                              setModelSearchInput(value)
                            }
                            options={getFilteredModels().map((m) => ({
                              value: String(m.id),
                              label: m.name,
                            }))}
                            value={
                              specialPartData.model_id
                                ? {
                                  value: String(specialPartData.model_id),
                                  label:
                                    modelLabels[
                                    String(specialPartData.model_id)
                                    ] ||
                                    getFilteredModels().find(
                                      (m) =>
                                        String(m.id) ===
                                        String(specialPartData.model_id),
                                    )?.name ||
                                    "Select Model",
                                }
                                : null
                            }
                            onChange={(opt) =>
                              handleSpecialPartChange({
                                target: {
                                  name: "model_id",
                                  value: opt ? opt.value : "",
                                },
                              })
                            }
                            placeholder={
                              specialPartData.brand_id
                                ? "Search Model..."
                                : "Pick a brand first"
                            }
                            isDisabled={!specialPartData.brand_id}
                            isLoading={isModelsFetching}
                            isClearable
                          />
                        </div>
                        {hasPermission("brand model create") && (
                          <Button
                            size="icon"
                            onClick={handleOpenModelModal}
                            disabled={!specialPartData.brand_id}
                            type="button"
                          >
                            <Plus size={18} />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>
                        Required Quantity{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        type="number"
                        name="required_quantity"
                        value={specialPartData.required_quantity}
                        onChange={handleSpecialPartChange}
                        min="1"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>
                        Unit Cost <span className="text-destructive">*</span>
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          {currencySymbol}
                        </span>
                        <Input
                          type="number"
                          className="pl-8"
                          name="unit_cost"
                          value={specialPartData.unit_cost}
                          onChange={handleSpecialPartChange}
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Retail Price</Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                          {currencySymbol}
                        </span>
                        <Input
                          type="number"
                          className="pl-8"
                          name="retail_price"
                          value={specialPartData.retail_price}
                          onChange={handleSpecialPartChange}
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="details" className="space-y-4 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>
                        Supplier <span className="text-destructive">*</span>
                      </Label>
                      <ReactSelect
                        className="basic-single"
                        classNamePrefix="select"
                        onInputChange={(value) => setSupplierSearchInput(value)}
                        options={suppliers.map((s) => ({
                          value: String(s.id),
                          label: s.name,
                        }))}
                        value={
                          specialPartData.supplier_id
                            ? {
                              value: String(specialPartData.supplier_id),
                              label:
                                supplierLabels[
                                String(specialPartData.supplier_id)
                                ] ||
                                suppliers.find(
                                  (s) =>
                                    String(s.id) ===
                                    String(specialPartData.supplier_id),
                                )?.name ||
                                "Select Supplier",
                            }
                            : null
                        }
                        onChange={(opt) =>
                          handleSpecialPartChange({
                            target: {
                              name: "supplier_id",
                              value: opt ? opt.value : "",
                            },
                          })
                        }
                        placeholder="Search Supplier..."
                        isLoading={isSuppliersFetching}
                        isClearable
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                        name="status"
                        value={specialPartData.status}
                        onChange={handleSpecialPartChange}
                      >
                        <option value="pending">Pending</option>
                        <option value="ordered">Ordered</option>
                        <option value="shipped">Shipped</option>
                        <option value="received">Received</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label>Order Date</Label>
                      <Input
                        type="datetime-local"
                        name="order_date"
                        value={specialPartData.order_date}
                        onChange={handleSpecialPartChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tracking ID</Label>
                      <Input
                        type="text"
                        name="tracking_id"
                        value={specialPartData.tracking_id}
                        onChange={handleSpecialPartChange}
                        maxLength={255}
                      />
                    </div>
                    <div className="col-span-1 md:col-span-2 space-y-2">
                      <Label>Order Link</Label>
                      <Input
                        type="url"
                        name="order_link"
                        value={specialPartData.order_link}
                        onChange={handleSpecialPartChange}
                        maxLength={255}
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="settings" className="space-y-4 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="has_purchase_order"
                        checked={specialPartData.has_purchase_order}
                        onCheckedChange={(checked) =>
                          handleSpecialPartBooleanChange({
                            target: {
                              name: "has_purchase_order",
                              checked: checked,
                            },
                          })
                        }
                      />
                      <Label htmlFor="has_purchase_order">
                        Has Purchase Order
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="is_tax_exclusive"
                        checked={specialPartData.is_tax_exclusive}
                        onCheckedChange={(checked) =>
                          handleSpecialPartBooleanChange({
                            target: {
                              name: "is_tax_exclusive",
                              checked: checked,
                            },
                          })
                        }
                      />
                      <Label htmlFor="is_tax_exclusive">Tax Exclusive</Label>
                    </div>
                    {specialPartData.has_purchase_order && (
                      <div className="col-span-1 md:col-span-2 space-y-2">
                        <Label>
                          Purchase Order{" "}
                          <span className="text-destructive">*</span>
                        </Label>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:border-primary disabled:cursor-not-allowed disabled:opacity-50"
                          name="purchase_order_id"
                          value={specialPartData.purchase_order_id}
                          onChange={handleSpecialPartChange}
                          required={specialPartData.has_purchase_order}
                        >
                          <option value="">Select Purchase Order</option>
                          {purchaseOrders.map((po) => (
                            <option key={po.id} value={po.id}>
                              {po.po_number} -{" "}
                              {po.supplier?.name || "Unknown Supplier"}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="col-span-1 md:col-span-2 space-y-2">
                      <Label>Order Notes</Label>
                      <div className="border rounded-md overflow-hidden bg-background">
                        <DefaultEditor
                          value={specialPartData.order_notes}
                          onChange={(e) =>
                            setSpecialPartData({
                              ...specialPartData,
                              order_notes: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              {/* Cost Summary */}
              {specialPartData.unit_cost > 0 &&
                specialPartData.required_quantity > 0 && (
                  <Alert
                    variant="info"
                    className="bg-blue-50 text-blue-800 border-blue-200 mt-4"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm w-full">
                      <div>
                        <strong>Quantity:</strong>{" "}
                        {specialPartData.required_quantity}
                      </div>
                      <div>
                        <strong>Unit Cost:</strong> {currencySymbol}
                        {parseFloat(specialPartData.unit_cost).toFixed(2)}
                      </div>
                      <div>
                        <strong>Total Cost:</strong> {currencySymbol}
                        {(
                          parseFloat(specialPartData.unit_cost) *
                          parseInt(specialPartData.required_quantity)
                        ).toFixed(2)}
                      </div>
                    </div>
                  </Alert>
                )}

              <DialogFooter className="px-5 pb-5">
                <Button
                  variant="outline"
                  onClick={handleCloseSpecialPartsModal}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    toast.info("Special Part Order", {
                      description:
                        "This special part order will be created after the repair job is successfully saved.",
                    });
                    handleCloseSpecialPartsModal();
                  }}
                  disabled={modalLoading}
                >
                  {modalLoading ? (
                    <>
                      <Spinner className="mr-2 h-4 w-4" /> Saving...
                    </>
                  ) : (
                    "Save"
                  )}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal for adding brand */}
      <Dialog open={brandModalOpen} onOpenChange={setBrandModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Brand</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>
                Brand Name <span className="text-destructive">*</span>
              </Label>
              <Input
                type="text"
                value={newBrand.name}
                onChange={(e) =>
                  setNewBrand({ ...newBrand, name: e.target.value })
                }
                placeholder="Enter brand name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBrandModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddBrand} disabled={modalLoading}>
              {modalLoading ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" /> Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal for adding model */}
      <Dialog open={modelModalOpen} onOpenChange={setModelModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Model</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>
                Model Name <span className="text-destructive">*</span>
              </Label>
              <Input
                type="text"
                value={newModel.name}
                onChange={(e) =>
                  setNewModel({ ...newModel, name: e.target.value })
                }
                placeholder="Enter model name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModelModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddModel} disabled={modalLoading}>
              {modalLoading ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" /> Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <InventorySelectorModal
        show={showInventorySelector}
        onHide={() => {
          setShowInventorySelector(false);
          setInventorySelectorTarget(null);
        }}
        type={inventorySelectorTarget?.type || "serial"}
        availableItems={inventorySelectorItems}
        selectedItems={
          inventorySelectorTarget?.partIndex != null
            ? inventorySelectorTarget.type === "standard"
              ? repairJob.selectedParts[inventorySelectorTarget.partIndex]
                ?.inventory_batch_id
                ? [
                  repairJob.selectedParts[inventorySelectorTarget.partIndex]
                    ?.inventory_batch_id,
                ]
                : []
              : repairJob.selectedParts[inventorySelectorTarget.partIndex]
                ?.serial_numbers || []
            : []
        }
        onSelect={handleInventorySelect}
        quantity={inventorySelectorQuantity}
      />
    </div>
  );
};

export default EditRepairJob;
